import { router, useForm, usePage } from "@inertiajs/react"
import { ChevronDown, ChevronRight, PlusIcon } from "lucide-react"
import { type ReactNode, useEffect, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AdminLayout } from "@/layouts/AdminLayout"
import { IconPicker } from "@/components/shared/IconPicker"
import { PoiIcon } from "@/lib/poi-icon"
import type { PoiCategoryItem, PoiCategoryLeaf, TimeContext } from "@/lib/types"
import type { SharedProps } from "@/types"

interface Props {
  categories: PoiCategoryItem[]
  urls: { index: string; create: string; reorder: string }
}

const TIME_CONTEXT_LABELS: Record<TimeContext, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  all_day: "All Day",
}

const TIME_CONTEXT_OPTIONS: { value: TimeContext | ""; label: string }[] = [
  { value: "", label: "None" },
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" },
  { value: "all_day", label: "All Day" },
]

function swapAdjacent<T extends { id: number }>(
  items: T[],
  id: number,
  direction: "up" | "down",
): T[] {
  const idx = items.findIndex((i) => i.id === id)
  if (idx === -1) return items
  if (direction === "up" && idx === 0) return items
  if (direction === "down" && idx === items.length - 1) return items
  const next = [...items]
  const swapIdx = direction === "up" ? idx - 1 : idx + 1
  ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
  return next
}

interface ReorderButtonsProps {
  id: number
  isFirst: boolean
  isLast: boolean
  onMove: (id: number, direction: "up" | "down") => void
}

function ReorderButtons({ id, isFirst, isLast, onMove }: ReorderButtonsProps) {
  return (
    <div className="flex gap-0.5">
      <Button
        variant="ghost"
        size="icon"
        className="size-6"
        disabled={isFirst}
        onClick={() => onMove(id, "up")}
        aria-label="Move up"
      >
        ↑
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-6"
        disabled={isLast}
        onClick={() => onMove(id, "down")}
        aria-label="Move down"
      >
        ↓
      </Button>
    </div>
  )
}

interface EditCategorySheetProps {
  category: PoiCategoryItem | PoiCategoryLeaf
  open: boolean
  onClose: () => void
}

function EditCategorySheet({ category, open, onClose }: EditCategorySheetProps) {
  const { data, setData, patch, processing, errors } = useForm({
    name: category.name,
    icon: category.icon ?? "",
    default_time_context: category.default_time_context ?? "",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(category.url, { onSuccess: onClose })
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit category</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
          <div className="grid gap-1.5">
            <Label htmlFor="edit-name">Name</Label>
            <div className="flex items-center gap-2">
              <IconPicker value={data.icon} onChange={(name) => setData("icon", name)} />
              <Input
                id="edit-name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                required
                className="flex-1"
              />
            </div>
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="edit-time-context">Default time context</Label>
            <Select
              value={data.default_time_context || "none"}
              onValueChange={(v) => setData("default_time_context", v === "none" ? "" : v)}
            >
              <SelectTrigger id="edit-time-context">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {TIME_CONTEXT_OPTIONS.filter((o) => o.value !== "").map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}

interface AddChildSheetProps {
  parentId: number
  parentName: string
  open: boolean
  onClose: () => void
  createUrl: string
}

function AddChildSheet({ parentId, parentName, open, onClose, createUrl }: AddChildSheetProps) {
  const { data, setData, post, processing, reset, errors } = useForm({
    name: "",
    slug: "",
    icon: "",
    position: "0",
    default_time_context: "",
    parent_id: String(parentId),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(createUrl, {
      onSuccess: () => {
        reset()
        onClose()
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Add sub-category to {parentName}</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
          <div className="grid gap-1.5">
            <Label htmlFor="child-name">Name</Label>
            <div className="flex items-center gap-2">
              <IconPicker value={data.icon} onChange={(name) => setData("icon", name)} />
              <Input
                id="child-name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                required
                className="flex-1"
              />
            </div>
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="child-slug">Slug</Label>
            <Input
              id="child-slug"
              value={data.slug}
              onChange={(e) => setData("slug", e.target.value)}
              required
            />
            {errors.slug && <p className="text-xs text-destructive">{errors.slug}</p>}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="child-time-context">Default time context</Label>
            <Select
              value={data.default_time_context || "none"}
              onValueChange={(v) => setData("default_time_context", v === "none" ? "" : v)}
            >
              <SelectTrigger id="child-time-context">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {TIME_CONTEXT_OPTIONS.filter((o) => o.value !== "").map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? "Adding…" : "Add sub-category"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}

interface ChildRowProps {
  child: PoiCategoryLeaf
  isFirst: boolean
  isLast: boolean
  onEdit: (c: PoiCategoryLeaf) => void
  onMove: (id: number, direction: "up" | "down") => void
  isAdmin: boolean
}

function ChildRow({ child, isFirst, isLast, onEdit, onMove, isAdmin }: ChildRowProps) {
  return (
    <TableRow className="bg-muted/30">
      <TableCell className="pl-10 text-sm text-muted-foreground">
        <div className="flex min-w-0 items-center gap-1">
          <span className="shrink-0 opacity-40">└</span>
          <span className="truncate">{child.name}</span>
          <span className="min-w-0 shrink truncate text-xs opacity-50">/{child.slug}</span>
        </div>
      </TableCell>
      <TableCell className="text-center">
        <div className="flex items-center justify-center">
          {child.icon
            ? <PoiIcon name={child.icon} size={16} className="text-muted-foreground" />
            : <span className="text-xs text-muted-foreground">—</span>}
        </div>
      </TableCell>
      <TableCell>
        {child.default_time_context ? (
          <Badge variant="outline" className="text-xs">
            {TIME_CONTEXT_LABELS[child.default_time_context]}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-right text-sm">{child.position}</TableCell>
      <TableCell className="text-right text-sm">{child.pois_count}</TableCell>
      <TableCell>
        {isAdmin && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onEdit(child)}>
              Edit
            </Button>
            <ReorderButtons
              id={child.id}
              isFirst={isFirst}
              isLast={isLast}
              onMove={onMove}
            />
          </div>
        )}
      </TableCell>
    </TableRow>
  )
}

interface CategoryRowProps {
  category: PoiCategoryItem
  isFirst: boolean
  isLast: boolean
  expanded: boolean
  onToggle: () => void
  onEdit: (c: PoiCategoryItem) => void
  onAddChild: (c: PoiCategoryItem) => void
  onRootMove: (id: number, direction: "up" | "down") => void
  onChildMove: (parentId: number, childId: number, direction: "up" | "down") => void
  onEditChild: (c: PoiCategoryLeaf) => void
  isAdmin: boolean
  localChildren: PoiCategoryLeaf[]
}

function CategoryRow({
  category,
  isFirst,
  isLast,
  expanded,
  onToggle,
  onEdit,
  onAddChild,
  onRootMove,
  onChildMove,
  onEditChild,
  isAdmin,
  localChildren,
}: CategoryRowProps) {
  return (
    <>
      <TableRow>
        <TableCell className="font-medium">
          <button
            type="button"
            className="flex min-w-0 items-center gap-1 hover:underline"
            onClick={onToggle}
          >
            {localChildren.length > 0 ? (
              expanded ? (
                <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
              )
            ) : (
              <span className="size-3.5 shrink-0" />
            )}
            <span className="truncate">{category.name}</span>
          </button>
          <span className="ml-1.5 block truncate text-xs text-muted-foreground">/{category.slug}</span>
        </TableCell>
        <TableCell className="text-center">
          <div className="flex items-center justify-center">
            {category.icon
              ? <PoiIcon name={category.icon} size={16} className="text-muted-foreground" />
              : <span className="text-xs text-muted-foreground">—</span>}
          </div>
        </TableCell>
        <TableCell>
          {category.default_time_context ? (
            <Badge variant="outline" className="text-xs">
              {TIME_CONTEXT_LABELS[category.default_time_context]}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-right text-sm">{category.position}</TableCell>
        <TableCell className="text-right text-sm">{category.pois_count}</TableCell>
        <TableCell>
          {isAdmin && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onEdit(category)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onAddChild(category)}
              >
                <PlusIcon className="size-3" />
                Child
              </Button>
              <ReorderButtons
                id={category.id}
                isFirst={isFirst}
                isLast={isLast}
                onMove={onRootMove}
              />
            </div>
          )}
        </TableCell>
      </TableRow>

      {expanded &&
        localChildren.map((child, idx) => (
          <ChildRow
            key={child.id}
            child={child}
            isFirst={idx === 0}
            isLast={idx === localChildren.length - 1}
            onEdit={onEditChild}
            onMove={(id, dir) => onChildMove(category.id, id, dir)}
            isAdmin={isAdmin}
          />
        ))}
    </>
  )
}

export default function PoiCategoriesIndex({ categories, urls }: Props) {
  const { props } = usePage<SharedProps>()
  const isAdmin = props.user_context?.is_admin ?? false

  const [localCategories, setLocalCategories] = useState<PoiCategoryItem[]>(categories)
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())

  useEffect(() => {
    setLocalCategories(categories)
  }, [categories])
  const [editTarget, setEditTarget] = useState<PoiCategoryItem | PoiCategoryLeaf | null>(null)
  const [addChildTarget, setAddChildTarget] = useState<PoiCategoryItem | null>(null)

  function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function handleRootMove(id: number, direction: "up" | "down") {
    const reordered = swapAdjacent(localCategories, id, direction)
    setLocalCategories(reordered)
    router.patch(
      urls.reorder,
      { ids: reordered.map((c) => c.id) },
      { preserveScroll: true },
    )
  }

  function handleChildMove(parentId: number, childId: number, direction: "up" | "down") {
    setLocalCategories((prev) =>
      prev.map((cat) => {
        if (cat.id !== parentId) return cat
        const reorderedChildren = swapAdjacent(cat.children, childId, direction)
        router.patch(
          urls.reorder,
          { ids: reorderedChildren.map((c) => c.id) },
          { preserveScroll: true },
        )
        return { ...cat, children: reorderedChildren }
      }),
    )
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">POI Categories</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Hierarchical categories for points of interest across the platform.
          </p>
        </div>
        {isAdmin && (
          <Button
            size="sm"
            onClick={() =>
              setAddChildTarget({
                id: 0,
                name: "",
                slug: "",
                icon: null,
                position: 0,
                default_time_context: null,
                pois_count: 0,
                children: [],
              })
            }
          >
            <PlusIcon className="mr-1.5 size-4" />
            New root category
          </Button>
        )}
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table className="table-fixed">
          <colgroup>
            <col />
            <col className="w-12" />
            <col className="w-36" />
            <col className="w-16" />
            <col className="w-16" />
            <col className="w-44" />
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-center">Icon</TableHead>
              <TableHead>Time ctx</TableHead>
              <TableHead className="text-right">Position</TableHead>
              <TableHead className="text-right">POIs</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {localCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  No categories found.
                </TableCell>
              </TableRow>
            ) : (
              localCategories.map((category, idx) => (
                <CategoryRow
                  key={category.id}
                  category={category}
                  localChildren={category.children}
                  isFirst={idx === 0}
                  isLast={idx === localCategories.length - 1}
                  expanded={expandedIds.has(category.id)}
                  onToggle={() => toggleExpand(category.id)}
                  onEdit={setEditTarget}
                  onAddChild={setAddChildTarget}
                  onRootMove={handleRootMove}
                  onChildMove={handleChildMove}
                  onEditChild={setEditTarget}
                  isAdmin={isAdmin}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {editTarget && (
        <EditCategorySheet
          category={editTarget}
          open={true}
          onClose={() => setEditTarget(null)}
        />
      )}

      {addChildTarget && addChildTarget.id !== 0 && (
        <AddChildSheet
          parentId={addChildTarget.id}
          parentName={addChildTarget.name}
          open={true}
          onClose={() => setAddChildTarget(null)}
          createUrl={urls.create}
        />
      )}

      {addChildTarget && addChildTarget.id === 0 && (
        <NewRootCategorySheet open={true} onClose={() => setAddChildTarget(null)} createUrl={urls.create} />
      )}
    </div>
  )
}

interface NewRootCategorySheetProps {
  open: boolean
  onClose: () => void
  createUrl: string
}

function NewRootCategorySheet({ open, onClose, createUrl }: NewRootCategorySheetProps) {
  const { data, setData, post, processing, reset, errors } = useForm({
    name: "",
    slug: "",
    icon: "",
    position: "0",
    default_time_context: "",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(createUrl, {
      onSuccess: () => {
        reset()
        onClose()
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New root category</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
          <div className="grid gap-1.5">
            <Label htmlFor="root-name">Name</Label>
            <div className="flex items-center gap-2">
              <IconPicker value={data.icon} onChange={(name) => setData("icon", name)} />
              <Input
                id="root-name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                required
                className="flex-1"
              />
            </div>
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="root-slug">Slug</Label>
            <Input
              id="root-slug"
              value={data.slug}
              onChange={(e) => setData("slug", e.target.value)}
              required
            />
            {errors.slug && <p className="text-xs text-destructive">{errors.slug}</p>}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="root-position">Position</Label>
            <Input
              id="root-position"
              type="number"
              value={data.position}
              onChange={(e) => setData("position", e.target.value)}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="root-time-context">Default time context</Label>
            <Select
              value={data.default_time_context || "none"}
              onValueChange={(v) => setData("default_time_context", v === "none" ? "" : v)}
            >
              <SelectTrigger id="root-time-context">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {TIME_CONTEXT_OPTIONS.filter((o) => o.value !== "").map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? "Creating…" : "Create category"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}

PoiCategoriesIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
